import { useTranslation } from 'react-i18next'
import { getIcon } from '../../common/components/icons/utils'
import { TGameCard } from '../../types/commonTypes'
import styles from './GameRatingModal.module.css'
import { useOnEscPress } from '../../hooks/useOnEscPress'
import { useDisableBodyScroll } from '../../hooks/useDisableBodyScroll'
import { SmallModal } from '../../common/components/Modal/SmallModal'

export type ViewGameRatingsModalProps = {
  game: TGameCard
  onClose: (game: TGameCard | null) => void
}

export const ViewGameRatingsModal: React.FC<ViewGameRatingsModalProps> = ({ game, onClose }) => {
  const { t } = useTranslation()
  useOnEscPress(() => onClose(null))
  useDisableBodyScroll()
  const avarageStars: number = game.rating ? +game.rating.average : 0

  return (
    <SmallModal
      onClose={() => onClose(null)}
      headerText={t('rating.rating_modal.rating_header', 'Rating')}
      iconName='star'
      hideSendButton={true}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalDescriptionBox}>
          <h4>"{game.gameName}"</h4>
          <div className={styles.ratingContainer}>
            <p className={styles.ratingNumber}>{avarageStars}</p>
            <div className={styles.ratedStars}>
              <div className={styles.ratedStar} key='first-star'>
                {getIcon(avarageStars >= 1 ? 'star' : avarageStars >= 0.5 ? 'starHalf' : 'starOutline')}
              </div>
              <div className={styles.ratedStar} key='second-star'>
                {getIcon(avarageStars >= 2 ? 'star' : avarageStars >= 1.5 ? 'starHalf' : 'starOutline')}
              </div>
              <div className={styles.ratedStar} key='third-star'>
                {getIcon(avarageStars >= 3 ? 'star' : avarageStars >= 2.5 ? 'starHalf' : 'starOutline')}
              </div>
              <div className={styles.ratedStar} key='fourth-star'>
                {getIcon(avarageStars >= 4 ? 'star' : avarageStars >= 3.5 ? 'starHalf' : 'starOutline')}
              </div>
              <div className={styles.ratedStar} key='fifth-star'>
                {getIcon(avarageStars >= 5 ? 'star' : avarageStars >= 4.5 ? 'starHalf' : 'starOutline')}
              </div>
            </div>
          </div>
          <p className={styles.ratingAmountText}>
            {game.rating?.average ?? 0} / 5 {'('}
            {game.rating?.count ?? 0} {t('rating.rating_modal.ratings.ratings_amount', 'ratings')}
            {')'}
          </p>
        </div>
        <h3 className={styles.ratingsHeading}>{t('rating.rating_modal.ratings.header', 'Ratings:')}</h3>
        <div className={styles.allRatingsContainer}>
          {game.rating?.ratings.map((rating, index) => {
            return (
              <div className={styles.singleRatingContainer} key={`${index}-rating`}>
                <div className={styles.singleRatingStars}>
                  <div className={styles.singleRatingStar} key={`${index}-single-first-star`}>
                    {getIcon(rating.value >= 1 ? 'star' : rating.value >= 0.5 ? 'starHalf' : 'starOutline')}
                  </div>
                  <div className={styles.singleRatingStar} key={`${index}-single-second-star`}>
                    {getIcon(rating.value >= 2 ? 'star' : rating.value >= 1.5 ? 'starHalf' : 'starOutline')}
                  </div>
                  <div className={styles.singleRatingStar} key={`${index}-single-third-star`}>
                    {getIcon(rating.value >= 3 ? 'star' : rating.value >= 2.5 ? 'starHalf' : 'starOutline')}
                  </div>
                  <div className={styles.singleRatingStar} key={`${index}-single-fourth-star`}>
                    {getIcon(rating.value >= 4 ? 'star' : rating.value >= 3.5 ? 'starHalf' : 'starOutline')}
                  </div>
                  <div className={styles.singleRatingStar} key={`${index}-single-fifth-star`}>
                    {getIcon(rating.value >= 5 ? 'star' : rating.value >= 4.5 ? 'starHalf' : 'starOutline')}
                  </div>
                </div>
                <p className={styles.ratingUser}>-{rating.user}</p>
                {rating && rating.comment && rating.comment.length > 0 && <p>"{rating.comment}"</p>}
              </div>
            )
          })}
        </div>
      </div>
    </SmallModal>
  )
}
